import { useEffect, useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';

import { useNetwork } from '../../lib/network-context';
import { highlightedJson } from '../../lib/json-tree';

export type ConsoleStatus =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'success'; signature: string; newAddress?: string }
  | { kind: 'error'; message: string; code?: string };

export type FetchStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; data: unknown }
  | { kind: 'notfound' }
  | { kind: 'error'; message: string };

interface ConsoleProps {
  /**
   * `instruction` — full submit-then-fetch flow (default).
   * `fetch-only` — no submit CTA, just a fetch input + result.
   * `fetch-all` — no input, single "Fetch all" button + list result.
   */
  mode?: 'instruction' | 'fetch-only' | 'fetch-all';
  accountType: string;
  /** Submission state from the parent's transaction runner. */
  status: ConsoleStatus;
  /** Triggered when the user clicks "Try it" — parent runs the SDK call. */
  onSubmit?: () => void;
  /** Reset back to idle. */
  onReset: () => void;
  /** Allow override of which Solscan cluster is appended for explorer links. */
  network?: string;
  /** Resolve & decode an address. Parent owns the SDK call. */
  onFetch: (address: string, accountType: string) => Promise<FetchStatus>;
  /** Resolve & decode every account of this type. Required for `fetch-all`. */
  onFetchAll?: (accountType: string) => Promise<FetchStatus>;
}

// Public mainnet endpoints (and most free RPCs) disable getProgramAccounts.
// Detect the well-known free endpoints so we can warn the user before they
// click "Fetch all" and stare at a Solana error #8100002.
function isPublicMainnetRpc(url: string | undefined): boolean {
  if (!url) return false;
  return /(api\.mainnet-beta\.solana\.com|api\.devnet\.solana\.com|api\.testnet\.solana\.com)/i.test(
    url
  );
}

function explorerSuffix(network: string | undefined, rpcUrl: string): string {
  if (network === 'mainnet-beta' || network === undefined) return '';
  if (network === 'devnet') return '?cluster=devnet';
  // atlasnet is a Star Atlas mainnet fork that solscan doesn't know about,
  // and `custom` is whatever the user pasted — both need
  // `?cluster=custom&customUrl=<rpc>` for solscan to resolve transactions.
  if (network === 'atlasnet' || network === 'custom') {
    return `?cluster=custom&customUrl=${encodeURIComponent(rpcUrl)}`;
  }
  return '';
}

/**
 * Inline text-link copy. Renders like an `<a>` (sibling to the
 * "view on solscan" link below the result) — labelled action plus a
 * small clipboard icon. Flips to a green check + "Copied" for ~1s
 * after click.
 */
function CopyTextLink({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* silent */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      type="button"
      className="console-link-btn"
      onClick={onCopy}
      title={copied ? 'Copied' : `${label} · ${text}`}
    >
      {copied ? (
        <>
          <Check
            size={11}
            strokeWidth={1.8}
            color="var(--mint)"
            style={{ marginRight: 6, verticalAlign: '-1px' }}
          />
          Copied
        </>
      ) : (
        <>
          {label}{' '}
          <Copy size={11} strokeWidth={1.6} style={{ marginLeft: 4, verticalAlign: '-1px' }} />
        </>
      )}
    </button>
  );
}

function CopyChipBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="cb-icon-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          /* silent */
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1100);
      }}
      aria-label="Copy"
    >
      {copied ? (
        <Check size={13} strokeWidth={1.8} color="var(--mint)" />
      ) : (
        <Copy size={13} strokeWidth={1.6} />
      )}
    </button>
  );
}

export function Console({
  mode = 'instruction',
  accountType,
  status,
  onSubmit,
  onReset,
  network,
  onFetch,
  onFetchAll,
}: ConsoleProps) {
  const net = useNetwork();
  const cluster = explorerSuffix(network ?? net.network, net.rpcUrl);

  const [fetchAddr, setFetchAddr] = useState('');
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>({ kind: 'idle' });

  // Reset fetch state when the parent's submission state goes back to idle
  // (e.g. instruction switch).
  useEffect(() => {
    if (status.kind === 'idle') {
      setFetchStatus({ kind: 'idle' });
      setFetchAddr('');
    }
  }, [status.kind, accountType]);

  const runFetch = async (addr: string) => {
    if (!addr.trim()) return;
    setFetchStatus({ kind: 'loading' });
    const result = await onFetch(addr.trim(), accountType);
    setFetchStatus(result);
  };

  const runFetchAll = async () => {
    if (!onFetchAll) return;
    setFetchStatus({ kind: 'loading' });
    const result = await onFetchAll(accountType);
    setFetchStatus(result);
  };

  const showHeader = status.kind !== 'idle';
  const headerLabel =
    status.kind === 'running'
      ? 'submitting…'
      : status.kind === 'success'
        ? 'submitted'
        : status.kind === 'error'
          ? 'failed'
          : 'awaiting submit';

  return (
    <div className="console" data-state={status.kind}>
      {showHeader && (
        <div className="console-head">
          <span className="dot" />
          <span>{headerLabel}</span>
          {(status.kind === 'success' || status.kind === 'error') && (
            <button type="button" className="reset" onClick={onReset}>
              reset
            </button>
          )}
        </div>
      )}

      <div className="console-body">
        {status.kind === 'idle' && mode === 'instruction' && (
          <>
            <div className="console-cta">
              <div>
                <span className="lbl-line">submit instruction</span>
                <div className="hint">Signs &amp; broadcasts via your connected wallet.</div>
              </div>
              <button
                type="button"
                className="sly-btn primary"
                onClick={onSubmit}
                disabled={!onSubmit}
              >
                Try it
              </button>
            </div>

            <div className="console-divider">
              <span>or inspect existing</span>
            </div>

            <ManualFetchInput
              accountType={accountType}
              value={fetchAddr}
              onChange={setFetchAddr}
              loading={fetchStatus.kind === 'loading'}
              onSubmit={() => runFetch(fetchAddr)}
            />

            {fetchStatus.kind !== 'idle' && <FetchResult status={fetchStatus} />}
          </>
        )}

        {status.kind === 'idle' && mode === 'fetch-only' && (
          <>
            <ManualFetchInput
              accountType={accountType}
              value={fetchAddr}
              onChange={setFetchAddr}
              loading={fetchStatus.kind === 'loading'}
              onSubmit={() => runFetch(fetchAddr)}
            />
            {fetchStatus.kind !== 'idle' && <FetchResult status={fetchStatus} />}
          </>
        )}

        {status.kind === 'idle' && mode === 'fetch-all' && (
          <>
            <div className="console-cta">
              <div>
                <span className="lbl-line">scan program accounts</span>
                <div className="hint">
                  Calls getProgramAccounts with the {accountType} discriminator.
                </div>
              </div>
              <button
                type="button"
                className="sly-btn primary"
                onClick={runFetchAll}
                disabled={!onFetchAll || fetchStatus.kind === 'loading'}
              >
                {fetchStatus.kind === 'loading' ? 'Fetching…' : 'Fetch all'}
              </button>
            </div>
            {isPublicMainnetRpc(net.rpcUrl) && (
              <div className="console-warn">
                Public mainnet RPCs disable getProgramAccounts. This call will fail — switch to a
                custom RPC (Helius, Triton, QuickNode, etc.) in the network bar above.
              </div>
            )}
            {fetchStatus.kind !== 'idle' && <FetchResult status={fetchStatus} />}
          </>
        )}

        {status.kind === 'running' && (
          <div className="hint" style={{ color: 'var(--ink-dim)' }}>
            Building instruction · awaiting wallet signature · broadcasting…
          </div>
        )}

        {status.kind === 'success' && (
          <>
            {status.newAddress && (
              <div className="console-acct">
                <div className="console-acct-row">
                  <span className="console-acct-label">new {accountType} →</span>
                  <span className="console-acct-addr" title={status.newAddress}>
                    {status.newAddress}
                  </span>
                  <CopyChipBtn text={status.newAddress} />
                </div>
                <div className="console-acct-row">
                  <span
                    style={{
                      flex: 1,
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10.5,
                      color: 'var(--ink-mute)',
                      lineHeight: 1.5,
                    }}
                  >
                    Decode the on-chain state via{' '}
                    <span style={{ color: 'var(--cyan)' }}>
                      fetch{accountType.charAt(0).toUpperCase() + accountType.slice(1)}()
                    </span>
                    .
                  </span>
                  <button
                    type="button"
                    className="sly-btn"
                    disabled={fetchStatus.kind === 'loading'}
                    onClick={() => runFetch(status.newAddress!)}
                  >
                    {fetchStatus.kind === 'loading'
                      ? 'Fetching…'
                      : fetchStatus.kind === 'ok'
                        ? 'Refresh'
                        : 'Fetch state'}
                  </button>
                </div>
              </div>
            )}

            <div className="console-meta">
              <CopyTextLink text={status.signature} label="Copy signature" />
              <a
                href={`https://solscan.io/tx/${status.signature}${cluster}`}
                target="_blank"
                rel="noopener"
              >
                view on solscan <ExternalLink size={11} strokeWidth={1.6} />
              </a>
              <span style={{ marginLeft: 'auto' }}>{network ?? net.network}</span>
            </div>

            {fetchStatus.kind !== 'idle' && <FetchResult status={fetchStatus} />}
          </>
        )}

        {status.kind === 'error' && (
          <div className="console-err">
            {status.code && <span className="code">{status.code} · </span>}
            {status.message}
          </div>
        )}
      </div>
    </div>
  );
}

function ManualFetchInput({
  accountType,
  value,
  onChange,
  loading,
  onSubmit,
}: {
  accountType: string;
  value: string;
  onChange: (v: string) => void;
  loading: boolean;
  onSubmit: () => void;
}) {
  return (
    <div className="console-manual">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') onSubmit();
        }}
        placeholder={`paste a ${accountType} address…`}
        spellCheck={false}
      />
      <button type="button" disabled={loading || !value.trim()} onClick={onSubmit}>
        {loading ? '…' : 'Fetch'}
      </button>
    </div>
  );
}

function FetchResult({ status }: { status: FetchStatus }) {
  return (
    <div className="console-fetch">
      {status.kind === 'loading' && <span className="empty">resolving…</span>}
      {status.kind === 'notfound' && (
        <span className="empty err">Account not found at this address.</span>
      )}
      {status.kind === 'error' && <span className="empty err">{status.message}</span>}
      {status.kind === 'ok' && highlightedJson(status.data)}
    </div>
  );
}
